import { useState, useCallback, useRef } from 'react'
import api from '../services/api'

interface UploadProgress {
  fileName: string
  progress: number // 0-100
  status: 'pending' | 'uploading' | 'completed' | 'error'
  error?: string
  url?: string
}

export interface UploadedFile {
  url: string
  fileName: string
  fileType: string
  fileSize: number
}

export function useFileUpload() {
  const [uploads, setUploads] = useState<Map<string, UploadProgress>>(new Map())
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map())

  // Devolve os metadados do servidor (áudio pode ter sido convertido para OGG,
  // então nome/tipo/tamanho finais não são os do File original).
  const uploadFileDetailed = useCallback(async (file: File): Promise<UploadedFile> => {
    const uploadId = `${file.name}-${Date.now()}`
    const abortController = new AbortController()
    abortControllersRef.current.set(uploadId, abortController)
    
    setUploads(prev => new Map(prev).set(uploadId, {
      fileName: file.name,
      progress: 0,
      status: 'uploading'
    }))

    try {
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await api.post('/upload/file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: abortController.signal,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || file.size))
          setUploads(prev => new Map(prev).set(uploadId, {
            fileName: file.name,
            progress,
            status: 'uploading'
          }))
        }
      })
      
      const fileUrl = response.data.url
      const uploaded: UploadedFile = {
        url: fileUrl,
        fileName: response.data.fileName || file.name,
        fileType: response.data.fileType || file.type || 'application/octet-stream',
        fileSize: response.data.fileSize ?? file.size,
      }

      setUploads(prev => new Map(prev).set(uploadId, {
        fileName: file.name,
        progress: 100,
        status: 'completed',
        url: fileUrl
      }))

      // Limpar abort controller
      abortControllersRef.current.delete(uploadId)

      return uploaded
    } catch (error: any) {
      const errorMessage = error.message || 'Erro no upload'
      
      setUploads(prev => new Map(prev).set(uploadId, {
        fileName: file.name,
        progress: 0,
        status: 'error',
        error: errorMessage
      }))
      
      // Limpar abort controller
      abortControllersRef.current.delete(uploadId)
      
      throw error
    }
  }, [])

  const uploadFile = useCallback(
    async (file: File): Promise<string> => (await uploadFileDetailed(file)).url,
    [uploadFileDetailed]
  )

  const cancelUpload = useCallback((uploadId: string) => {
    const controller = abortControllersRef.current.get(uploadId)
    if (controller) {
      controller.abort()
      abortControllersRef.current.delete(uploadId)
    }
    
    setUploads(prev => {
      const newMap = new Map(prev)
      newMap.delete(uploadId)
      return newMap
    })
  }, [])

  const clearUpload = useCallback((uploadId: string) => {
    setUploads(prev => {
      const newMap = new Map(prev)
      newMap.delete(uploadId)
      return newMap
    })
  }, [])

  const clearAllUploads = useCallback(() => {
    // Cancelar todos os uploads em andamento
    abortControllersRef.current.forEach(controller => controller.abort())
    abortControllersRef.current.clear()
    setUploads(new Map())
  }, [])

  return { 
    uploads, 
    uploadFile, 
    uploadFileDetailed,
    cancelUpload, 
    clearUpload, 
    clearAllUploads 
  }
}
